import React, { useState } from "react";
import { CHART_1, CHART_2, INK, MUTED, GRID, PAPER } from "./palette";

// ---- johnson_neyman: the focal predictor's slope across the moderator, with its 95% band ----
// Props from ChartData#johnson_neyman: grid [[w, slope, lower, upper]] across the moderator's observed
// range, boundary (the Johnson–Neyman point inside it), beyond (cases above it), moderatorValues
// (everyone's moderator score, drawn as a rug), tCrit, zeroAt (where the estimate crosses zero, if
// inside the range), focalName, moderatorName, outcomeName; xUnit/yUnit from settings.
// Where the band excludes zero is shaded green (the region of significance); the slope and band are
// teal, as in ConceptChart.jsx; zero and the boundary are ink and dashed muted lines.
const W = 640, TOP = 16, RIGHT = 16;

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const minus = (t) => (t < 0 ? `−${-t}` : t);
const scale = (ax, a, b) => (v) => a + ((v - ax.min) / (ax.max - ax.min)) * (b - a);
const lower = (s) => s.split(" ").map((w) => (/^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w)).join(" ");

function axis(xs, pad = 0.05) {
	const lo = Math.min(...xs), hi = Math.max(...xs), span = hi - lo || 1;
	const rough = span / 6, mag = 10 ** Math.floor(Math.log10(rough));
	const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough);
	const min = Math.floor((lo - span * pad) / step) * step, max = Math.ceil((hi + span * pad) / step) * step;
	const ticks = [];
	for (let t = min; t <= max + step / 2; t += step) ticks.push(+t.toFixed(10));
	return { min, max, ticks };
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, 16), W - RIGHT - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${TOP})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

// Runs of the grid where the interval excludes zero, as [from, to] in moderator units, with each
// edge interpolated to where the band's nearer bound crosses zero.
function significantRuns(grid) {
	const sig = (g) => g[2] > 0 || g[3] < 0;
	const edge = (a, b) => {
		const k = a[2] > 0 || b[2] > 0 ? 2 : 3;
		return a[0] + ((0 - a[k]) / (b[k] - a[k])) * (b[0] - a[0]);
	};
	const runs = [];
	let start = sig(grid[0]) ? grid[0][0] : null;
	for (let i = 1; i < grid.length; i += 1) {
		const was = sig(grid[i - 1]), is = sig(grid[i]);
		if (!was && is) start = edge(grid[i - 1], grid[i]);
		if (was && !is) { runs.push([start, edge(grid[i - 1], grid[i])]); start = null; }
	}
	if (start !== null) runs.push([start, grid[grid.length - 1][0]]);
	return runs;
}

export default function JohnsonNeymanChart({ grid, boundary, beyond, moderatorValues = [], tCrit, zeroAt, focalName, moderatorName, outcomeName, xUnit, yUnit, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const height = 320, box = { l: 48, r: W - RIGHT - 8, t: TOP + 30, b: height - 52 };
	const ws = grid.map((g) => g[0]);
	const axX = { ...axis(ws, 0), min: Math.min(...ws), max: Math.max(...ws) };
	axX.ticks = axX.ticks.filter((t) => t >= axX.min - 1e-9 && t <= axX.max + 1e-9);
	const axY = axis([0, ...grid.flatMap((g) => [g[2], g[3]])], 0.08);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const focal = lower(focalName), moderator = lower(moderatorName), outcome = lower(outcomeName);
	// An interpolated edge within 0.01 of the recompute's exact boundary is snapped to it, so the
	// shading meets the boundary line and the label can count the cases beyond it.
	const snap = (v) => (boundary != null && Math.abs(v - boundary) < 0.01 ? boundary : v);
	const runs = significantRuns(grid).map((r) => r.map(snap));
	// The stretches between runs, where the interval includes zero.
	const edges = [Math.min(...grid.map((g) => g[0])), ...runs.flat(), Math.max(...grid.map((g) => g[0]))];
	const gaps = [];
	for (let i = 0; i < edges.length; i += 2) if (edges[i + 1] - edges[i] > 1e-9) gaps.push([edges[i], edges[i + 1]]);
	const n = moderatorValues.length;
	const band = `M${grid.map((g) => `${x(g[0]).toFixed(1)},${y(g[3]).toFixed(1)}`).join(" L")} L${[...grid].reverse().map((g) => `${x(g[0]).toFixed(1)},${y(g[2]).toFixed(1)}`).join(" L")} Z`;
	const path = `M${grid.map((g) => `${x(g[0]).toFixed(1)},${y(g[1]).toFixed(1)}`).join(" L")}`;
	const per = `${yUnit ? `${yUnit}s` : outcome} per ${xUnit || `unit of ${focal}`}`;
	const above = (r) => grid.find((g) => g[0] >= r[0] && g[0] <= r[1])?.[2] > 0;
	const runText = runs.map((r) => `${fmt(r[0])} to ${fmt(r[1])}`).join(" and ");
	const counts = boundary != null && beyond != null ? `${n - beyond} of the ${n} ${noun}s score below it and ${beyond} above` : "";
	const step = (axX.max - axX.min) / (grid.length - 1);
	return (
		<figure>
			<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
				<span className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded" style={{ background: CHART_1 }} />{focalName}'s slope at each {moderator} score</span>
				<span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: CHART_1, opacity: 0.25 }} />95% confidence band</span>
				<span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: CHART_2, opacity: 0.3 }} />Band excludes zero (significant at α = .05)</span>
				{n > 0 && <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-px" style={{ background: MUTED }} />One {noun}'s {moderator}</span>}
			</div>
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Johnson–Neyman plot: ${focal}'s slope on ${outcome} across ${moderator} scores from ${fmt(axX.min)} to ${fmt(axX.max)}, with its 95% confidence band. The slope is ${fmt(grid[0][1])} at ${fmt(grid[0][0])} and ${fmt(grid[grid.length - 1][1])} at ${fmt(grid[grid.length - 1][0])}. ${runs.length ? `The band excludes zero from ${runText}` : "The band includes zero everywhere"}${boundary != null ? `; the boundary is at ${moderator} ${fmt(boundary)}` : ""}.`}>
				{runs.map((r, i) => (
					<g key={i}>
						<rect x={x(r[0])} y={box.t} width={x(r[1]) - x(r[0])} height={box.b - box.t} fill={CHART_2} opacity="0.12" />
						<text x={(x(r[0]) + x(r[1])) / 2} y={box.t - 8} fontSize="11" fill={INK} textAnchor="middle">
							{above(r) ? `Slope significantly above zero` : `Slope significantly below zero`}
						</text>
					</g>
				))}
				{gaps.filter((r) => x(r[1]) - x(r[0]) > 120).map((r, i) => (
					<text key={`gap${i}`} x={(x(r[0]) + x(r[1])) / 2} y={box.t - 8} fontSize="11" fill={MUTED} textAnchor="middle">
						Can't tell from zero{beyond != null && r[0] === boundary ? ` (${beyond} ${noun}s)` : ""}
					</text>
				))}
				{axY.ticks.map((t) => (
					<g key={`y${t}`}>
						<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{minus(t)}</text>
					</g>
				))}
				{axX.ticks.map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 30} fontSize="11" fill={MUTED} textAnchor="middle">{t}</text>)}
				<text x={box.r} y={box.b + 46} fontSize="11" fill={MUTED} textAnchor="end">{moderatorName}</text>
				<text x={box.l} y={box.t - 22} fontSize="11" fill={MUTED}>{focalName}'s slope ({per})</text>
				<line x1={box.l} x2={box.r} y1={y(0)} y2={y(0)} stroke={INK} strokeWidth="1.25" />
				<text x={box.l + 6} y={y(0) + 14} fontSize="11" fill={MUTED}>Zero slope</text>
				<path d={band} fill={CHART_1} opacity="0.18" />
				<path d={path} fill="none" stroke={CHART_1} strokeWidth="2.5" strokeLinejoin="round" />
				{moderatorValues.map((v, i) => (
					<line key={i} x1={x(v)} x2={x(v)} y1={box.b + 4} y2={box.b + 14} stroke={MUTED} strokeWidth="1" opacity="0.35" />
				))}
				{boundary != null && (
					<g>
						<line x1={x(boundary)} x2={x(boundary)} y1={box.t} y2={box.b} stroke={MUTED} strokeWidth="1.5" strokeDasharray="5 3" />
						<text x={x(boundary) + 5} y={box.b - 8} fontSize="12" fill={INK} fontWeight="600">{fmt(boundary)}</text>
					</g>
				)}
				{grid.map((g, i) => (
					<rect key={i} x={x(g[0]) - (x(axX.min + step) - x(axX.min)) / 2} y={box.t} width={x(axX.min + step) - x(axX.min)} height={box.b - box.t} fill="transparent"
						onMouseEnter={() => setHover({ i, cx: x(g[0]), lines: [`${moderatorName} ${fmt(g[0])}`, `${focalName}'s slope ${fmt(g[1])}, 95% CI [${fmt(g[2])}, ${fmt(g[3])}]`,
							g[2] > 0 || g[3] < 0 ? "The interval excludes zero" : "The interval includes zero"] })}
						onMouseLeave={() => setHover(null)} />
				))}
				{hover && <circle cx={x(grid[hover.i][0])} cy={y(grid[hover.i][1])} r="4.5" fill={CHART_1} stroke={PAPER} strokeWidth="2" pointerEvents="none" />}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				The line is {focal}'s slope on {outcome} for {noun}s at each {moderator} score, and the shaded band its 95% confidence interval (the slope ± {tCrit ? fmt(tCrit, 3) : "t*"} standard errors).
				{boundary != null ? ` Where the band stays clear of zero, the slope is significantly different from zero at α = .05; the Johnson–Neyman boundary, at ${fmt(boundary)}, is where the band's edge touches zero. ${counts ? `${counts.charAt(0).toUpperCase()}${counts.slice(1)}.` : ""}` : ""}
				{zeroAt != null ? ` The slope's estimate itself reaches zero only at ${fmt(zeroAt)}.` : ""}
				{" "}Each point on the band is its own test, so the boundary is approximate. The ticks along the bottom show where {noun}s' {moderator} scores fall: the band is narrowest near the average and widens toward the ends, where there are few.
			</figcaption>
		</figure>
	);
}
